<svelte:options runes={true} />

<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { onMount } from "svelte";
	import { PointSchema, type Point } from "#gen/bundles/v1/common_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { getContentType } from "#lib/components/images/utils.js";

	interface FocalPointProps {
		value: string;
		contentType: string | null | undefined;
		focalPoint: Point | undefined;
	}

	interface $$Events {
		convert: CustomEvent<{ data: string; focalX?: number; focalY: number }>;
	}

	let { value, contentType = null, focalPoint = $bindable() }: FocalPointProps = $props();
	let imageEl = $state<HTMLImageElement | null>(null);
	let containerEl = $state<HTMLDivElement | null>(null);
	let isDragging = $state(false);
	const focalPointPosition = $state({ x: 50, y: 50 }); // Default to center
	export async function reset() {
		focalPoint = create(PointSchema, { x: 0.5, y: 0.5 });
	}

	onMount(async () => {
		if (!contentType) {
			contentType = await getContentType(value);
		}

		// initialize focalPoint if undefined
		if (!focalPoint) {
			focalPoint = create(PointSchema, { x: 0.5, y: 0.5 });
		}
	});

	// calculate focal point position based on actual image bounds
	$effect(() => {
		if (focalPoint && containerEl && imageEl && imageEl.naturalWidth > 0 && imageEl.naturalHeight > 0) {
			calculateFocalPointPosition();
		}
	});

	$effect(() => {
		if (imageEl && focalPoint) {
			const img = imageEl;
			function handleLoad() {
				if (containerEl && img.naturalWidth > 0 && img.naturalHeight > 0 && focalPoint) {
					calculateFocalPointPosition();
				}
			}

			// Add load event listener
			img.addEventListener("load", handleLoad);

			// If image is already loaded, calculate position
			if (img.complete && img.naturalWidth > 0) {
				handleLoad();
			}

			// Cleanup function
			return () => {
				img.removeEventListener("load", handleLoad);
			};
		}
	});

	function calculateFocalPointPosition() {
		if (containerEl && imageEl && focalPoint) {
			const containerRect = containerEl.getBoundingClientRect();
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;
			const naturalWidth = imageEl.naturalWidth;
			const naturalHeight = imageEl.naturalHeight;
			const containerAspect = containerWidth / containerHeight;
			const imageAspect = naturalWidth / naturalHeight;

			let imageWidth, imageHeight, imageLeft, imageTop;

			if (imageAspect > containerAspect) {
				// Image is wider than container - fit to width
				imageWidth = containerWidth;
				imageHeight = containerWidth / imageAspect;
				imageLeft = 0;
				imageTop = (containerHeight - imageHeight) / 2;
			} else {
				// Image is taller than container - fit to height
				imageHeight = containerHeight;
				imageWidth = containerHeight * imageAspect;
				imageTop = 0;
				imageLeft = (containerWidth - imageWidth) / 2;
			}

			// Calculate position relative to container (in pixels)
			const pixelX = imageLeft + focalPoint.x * imageWidth;
			const pixelY = imageTop + focalPoint.y * imageHeight;

			// Convert to percentage relative to container
			focalPointPosition.x = (pixelX / containerWidth) * 100;
			focalPointPosition.y = (pixelY / containerHeight) * 100;
		}
	}

	function setFocalPoint(event: MouseEvent) {
		const containerRect = containerEl?.getBoundingClientRect();

		if (containerRect && imageEl) {
			const clickX = event.clientX - containerRect.left;
			const clickY = event.clientY - containerRect.top;
			const containerWidth = containerRect.width;
			const containerHeight = containerRect.height;
			const naturalWidth = imageEl.naturalWidth;
			const naturalHeight = imageEl.naturalHeight;

			const containerAspect = containerWidth / containerHeight;
			const imageAspect = naturalWidth / naturalHeight;

			let imageWidth, imageHeight, imageLeft, imageTop;

			if (imageAspect > containerAspect) {
				imageWidth = containerWidth;
				imageHeight = containerWidth / imageAspect;
				imageLeft = 0;
				imageTop = (containerHeight - imageHeight) / 2;
			} else {
				imageHeight = containerHeight;
				imageWidth = containerHeight * imageAspect;
				imageTop = 0;
				imageLeft = (containerWidth - imageWidth) / 2;
			}

			// if click is within the actual image bounds
			if (clickX >= imageLeft && clickX <= imageLeft + imageWidth && clickY >= imageTop && clickY <= imageTop + imageHeight) {
				const x = (clickX - imageLeft) / imageWidth;
				const y = (clickY - imageTop) / imageHeight;

				focalPoint = create(PointSchema, { x, y });
			}
		}
	}

	function handleMouseDown(event: MouseEvent) {
		isDragging = true;
		setFocalPoint(event);
	}

	function handleMouseMove(event: MouseEvent) {
		if (isDragging) {
			setFocalPoint(event);
		}
	}

	function handleMouseUp() {
		isDragging = false;
	}

	function onKeyDown(event: KeyboardEvent) {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
		}
	}
</script>

<div
	bind:this={containerEl}
	class="relative aspect-video w-full max-w-full overflow-hidden outline-none focus:outline-none"
	role="button"
	tabindex="0"
	aria-label="set focal point"
	onmousedown={handleMouseDown}
	onmouseup={handleMouseUp}
	onmousemove={handleMouseMove}
	onkeydown={onKeyDown}
	style="
		background-color: #999999;
		background-image:
			linear-gradient(45deg, #7A7A7A 25%, transparent 25%),
			linear-gradient(-45deg, #7A7A7A 25%, transparent 25%),
			linear-gradient(45deg, transparent 75%, #7A7A7A 75%),
			linear-gradient(-45deg, transparent 75%, #7A7A7A 75%);
		background-size: 16px 16px;
		background-position: 0 0, 0 8px, 8px -8px, -8px 0;
		cursor: crosshair;
	"
>
	<img bind:this={imageEl} src={value} alt="Focal point selector" class="h-full w-full object-contain select-none" draggable="false" />
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="47"
		height="47"
		viewBox="0 0 47 47"
		fill="none"
		class="pointer-events-none absolute"
		style="
			top: calc({focalPointPosition.y}% - 23.5px);
			left: calc({focalPointPosition.x}% - 23.5px);
		"
	>
		<circle opacity="0.23" cx="23.5" cy="23.5" r="23.5" fill="#FF00B3" />
		<circle opacity="0.34" cx="23.5" cy="23.5" r="14.5" fill="#FF00B3" />
		<circle cx="23.5" cy="23.5" r="7" fill="#FF00B3" stroke="white" />
	</svg>
</div>
<div class="min-h-12">
	<div class="mt-2 flex shrink-0 items-start gap-2 text-gray-700">
		<Icon name="exclamation-circle" class="h-5 w-5 shrink-0" />
		<P size="xs">Click to place the pink dot on the most important part of your image—like a face or logo—so we can center it perfectly on any screen size.</P>
	</div>
</div>
